import { InfoCard, Link, Progress } from '@backstage/core-components';
import {
  alertApiRef,
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { announcementUpdatePermission } from '@backstage-community/plugin-announcements-common';
import { EntityDisplayName } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  Button,
  Chip,
  IconButton,
  Popover,
  Theme,
  Tooltip,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CheckIcon from '@material-ui/icons/Check';
import PaletteIcon from '@material-ui/icons/Palette';
import { Alert } from '@material-ui/lab';
import { CSSProperties, MouseEvent, ReactNode, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  ANNOUNCEMENT_COLOR_KEYS,
  ANNOUNCEMENT_PALETTE,
  AnnouncementColorKey,
  fetchAnnouncementColors,
  paletteColor,
  resolveAnnouncementColor,
  saveAnnouncementColor,
} from './announcementColors';
import {
  ANNOUNCEMENTS_PATH,
  HomeAnnouncement,
  IncidentKind,
  announcementCategoryPath,
  announcementViewPath,
  fetchAnnouncements,
  toIncident,
} from './homeAnnouncements';
import { useHomeCardFooterStyles, ViewMoreLink } from './HomeCardFooter';
import { IncidentConfig, readStatusConfig } from './homeConfig';
import { formatRelative, useAsyncValue } from './homeUtils';

const NEW_FOR_DAYS = 7;

const useListStyles = makeStyles((theme: Theme) => ({
  list: {
    display: 'grid',
    gap: theme.spacing(2),
    padding: theme.spacing(0.5, 0),
  },
}));

const useBoxStyles = makeStyles((theme: Theme) => ({
  box: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    padding: theme.spacing(2, 2.5),
    border: '2px solid var(--announcement-color)',
    borderRadius: 14,
    background: 'var(--announcement-background)',
    boxShadow: 'var(--announcement-shadow)',
    transition: theme.transitions.create(['box-shadow', 'transform']),
    '&:hover, &:focus-within': {
      transform: 'translateY(-2px)',
      boxShadow: 'var(--announcement-shadow-hover)',
    },
    '&:hover $colorButton, &:focus-within $colorButton': {
      opacity: 1,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&:hover, &:focus-within': { transform: 'none' },
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.75, 2),
    },
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  title: {
    flex: 1,
    minWidth: 0,
    margin: 0,
    fontWeight: 700,
    fontSize: '1.05rem',
    lineHeight: 1.35,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    overflowWrap: 'anywhere',
    '& a': {
      color: theme.palette.text.primary,
    },
  },
  aside: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: theme.spacing(0.5),
    marginTop: 1,
  },
  chip: {
    height: 22,
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: 0.3,
  },
  categoryChip: {
    maxWidth: 160,
    color: 'var(--announcement-contrast)',
    backgroundColor: 'var(--announcement-color)',
    cursor: 'pointer',
    '&:hover, &:focus': {
      backgroundColor: 'var(--announcement-color)',
      filter: 'brightness(1.08)',
    },
  },
  newChip: {
    color: theme.palette.success.main,
    border: `1px solid ${alpha(theme.palette.success.main, 0.6)}`,
    backgroundColor: 'transparent',
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  metaIcon: {
    fontSize: 14,
  },
  divider: {
    width: 48,
    height: 2,
    border: 0,
    borderRadius: 1,
    margin: theme.spacing(1.25, 0),
    backgroundColor: 'var(--announcement-divider)',
  },
  excerpt: {
    color: theme.palette.text.secondary,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  colorButton: {
    padding: 3,
    color: 'var(--announcement-color)',
    opacity: 0.55,
    transition: theme.transitions.create('opacity'),
  },
}));
/** Colour-dependent values, set as CSS custom properties on each box. */
const announcementBoxVars = (theme: Theme, color: string): CSSProperties => {
  const dark = theme.palette.type === 'dark';
  return {
    '--announcement-color': color,
    '--announcement-contrast': theme.palette.getContrastText(color),
    '--announcement-divider': alpha(color, dark ? 0.55 : 0.4),
    '--announcement-background': `linear-gradient(135deg, ${alpha(color, dark ? 0.2 : 0.09)} 0%, ${alpha(
      color,
      dark ? 0.06 : 0.02,
    )} 100%), ${theme.palette.background.paper}`,
    '--announcement-shadow': `0 2px 10px ${alpha(color, dark ? 0.3 : 0.16)}`,
    '--announcement-shadow-hover': `0 8px 22px ${alpha(color, dark ? 0.4 : 0.26)}`,
  } as CSSProperties;
};
const usePickerStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(1.5, 2),
    maxWidth: 236,
  },
  swatches: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 36px)',
    gap: theme.spacing(0.75),
    margin: theme.spacing(1, 0),
  },
  swatch: {
    width: 36,
    height: 36,
    padding: 0,
    borderRadius: '50%',
    color: '#fff',
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow: `0 0 0 1px ${theme.palette.divider}`,
  },
  selected: {
    boxShadow: `0 0 0 2px ${theme.palette.text.primary}`,
  },
}));

interface ColorPickerProps {
  anchorEl: HTMLElement | null;
  selected?: AnnouncementColorKey;
  onClose: () => void;
  onSelect: (color: AnnouncementColorKey | null) => void;
}

const AnnouncementColorPicker = ({ anchorEl, selected, onClose, onSelect }: ColorPickerProps) => {
  const classes = usePickerStyles();
  const theme = useTheme();
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <div className={classes.root}>
        <Typography variant="subtitle2">Outline colour</Typography>
        <div className={classes.swatches} role="group" aria-label="Outline colour">
          {ANNOUNCEMENT_COLOR_KEYS.map(key => (
            <Tooltip key={key} title={ANNOUNCEMENT_PALETTE[key].label}>
              <IconButton
                aria-label={ANNOUNCEMENT_PALETTE[key].label}
                aria-pressed={selected === key}
                className={`${classes.swatch} ${selected === key ? classes.selected : ''}`}
                style={{ backgroundColor: paletteColor(key, theme) }}
                onClick={() => onSelect(key)}
              >
                {selected === key ? <CheckIcon fontSize="small" /> : <span />}
              </IconButton>
            </Tooltip>
          ))}
        </div>
        <Button
          size="small"
          fullWidth
          variant={selected ? 'outlined' : 'contained'}
          color="primary"
          onClick={() => onSelect(null)}
        >
          Automatic (by category)
        </Button>
      </div>
    </Popover>
  );
};

interface AnnouncementBoxProps {
  announcement: HomeAnnouncement;
  incidentKind?: IncidentKind;
  chosenColor?: AnnouncementColorKey;
  canEditColor: boolean;
  onColorChange: (color: AnnouncementColorKey | null) => void;
}

const AnnouncementBox = ({
  announcement,
  incidentKind,
  chosenColor,
  canEditColor,
  onColorChange,
}: AnnouncementBoxProps) => {
  const theme = useTheme();
  const color = resolveAnnouncementColor(theme, chosenColor, incidentKind);
  const classes = useBoxStyles();
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const isNew =
    Date.now() - Date.parse(announcement.created_at) <= NEW_FOR_DAYS * 24 * 3600 * 1000;

  const publisher = announcement.on_behalf_of || announcement.publisher;

  return (
    <article
      className={classes.box}
      style={announcementBoxVars(theme, color)}
      aria-label={announcement.title}
    >
      <div className={classes.header}>
        <Typography variant="subtitle1" component="h3" className={classes.title}>
          <Link to={announcementViewPath(announcement.id)}>{announcement.title}</Link>
        </Typography>
        <div className={classes.aside}>
          {isNew && <Chip size="small" label="New" className={`${classes.chip} ${classes.newChip}`} />}
          {announcement.category && (
            <Chip
              size="small"
              clickable
              label={announcement.category.title}
              className={`${classes.chip} ${classes.categoryChip}`}
              component={RouterLink}
              to={announcementCategoryPath(announcement.category.slug)}
            />
          )}
          {canEditColor && (
            <>
              <Tooltip title="Change outline colour">
                <IconButton
                  size="small"
                  aria-label={`Change outline colour of ${announcement.title}`}
                  className={classes.colorButton}
                  onClick={(event: MouseEvent<HTMLElement>) => setPickerAnchor(event.currentTarget)}
                >
                  <PaletteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <AnnouncementColorPicker
                anchorEl={pickerAnchor}
                selected={chosenColor}
                onClose={() => setPickerAnchor(null)}
                onSelect={next => {
                  setPickerAnchor(null);
                  onColorChange(next);
                }}
              />
            </>
          )}
        </div>
      </div>
      <Typography variant="caption" component="div" className={classes.meta}>
        <AccessTimeIcon className={classes.metaIcon} aria-hidden />
        <span>Posted {formatRelative(announcement.created_at)}</span>
        {publisher && (
          <>
            <span aria-hidden>·</span>
            <span>
              by <EntityDisplayName entityRef={publisher} defaultKind="group" hideIcon disableTooltip />
            </span>
          </>
        )}
      </Typography>
      {announcement.excerpt && (
        <>
          <hr className={classes.divider} />
          <Typography variant="body2" className={classes.excerpt}>
            {announcement.excerpt}
          </Typography>
        </>
      )}
    </article>
  );
};
const incidentKindOf = (
  announcement: HomeAnnouncement,
  config: IncidentConfig,
): IncidentKind | undefined => {
  const slug = announcement.category?.slug;
  if (slug === config.outageCategory) return 'outage';
  if (slug === config.maintenanceCategory) return 'maintenance';
  return undefined;
};

export interface HomeAnnouncementsCardProps {
  title?: string;
  max?: number;
}

/**
 * Announcements card for the home page. Each announcement is a left-aligned, outlined box (title and
 * category first, then who posted it and when, a short divider and the preview). Active
 * Outage/Maintenance announcements are pinned first. The outline colour follows the category
 * (red for outages, amber for maintenance, the theme colour otherwise) unless an admin picked
 * one with the palette button; picked colours are stored by the home-visits backend.
 */
export const HomeAnnouncementsCard = ({ title = 'Announcements', max = 5 }: HomeAnnouncementsCardProps) => {
  const classes = useListStyles();
  const footer = useHomeCardFooterStyles();
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const alertApi = useApi(alertApiRef);
  const incidentConfig = useMemo(() => readStatusConfig(configApi).incidents, [configApi]);
  const { allowed: canEditColor } = usePermission({ permission: announcementUpdatePermission });
  const [colorOverrides, setColorOverrides] = useState<Record<string, AnnouncementColorKey | null>>({});

  const { value, loading, error } = useAsyncValue(
    () => fetchAnnouncements(discoveryApi, fetchApi, { active: true, max: Math.max(max * 2, 10) }),
    [discoveryApi, fetchApi, max],
  );

  const announcements = useMemo(() => {
    const now = Date.now();
    const pinned = (a: HomeAnnouncement) => (toIncident(a, incidentConfig, now) ? 0 : 1);
    return [...(value ?? [])]
      .sort((a, b) => pinned(a) - pinned(b) || Date.parse(b.created_at) - Date.parse(a.created_at))
      .slice(0, max);
  }, [value, incidentConfig, max]);

  const idsKey = announcements.map(a => a.id).join(',');
  const { value: storedColors } = useAsyncValue(
    () =>
      fetchAnnouncementColors(discoveryApi, fetchApi, idsKey ? idsKey.split(',') : []).catch(
        () => ({}) as Record<string, AnnouncementColorKey>,
      ),
    [discoveryApi, fetchApi, idsKey],
  );

  const colorOf = (id: string): AnnouncementColorKey | undefined =>
    id in colorOverrides ? colorOverrides[id] ?? undefined : storedColors?.[id];

  const changeColor = async (announcement: HomeAnnouncement, color: AnnouncementColorKey | null) => {
    const previous = colorOverrides;
    setColorOverrides(current => ({ ...current, [announcement.id]: color }));
    try {
      await saveAnnouncementColor(discoveryApi, fetchApi, announcement.id, color);
    } catch (e) {
      setColorOverrides(previous);
      alertApi.post({
        message: `Could not change the colour of "${announcement.title}": ${
          e instanceof Error ? e.message : String(e)
        }`,
        severity: 'error',
      });
    }
  };

  let content: ReactNode;
  if (loading && !value) {
    content = <Progress />;
  } else if (error) {
    content = <Alert severity="info">Announcements are unavailable right now.</Alert>;
  } else if (announcements.length === 0) {
    content = (
      <Typography variant="body2" color="textSecondary">
        No announcements right now.
      </Typography>
    );
  } else {
    content = (
      <div className={classes.list}>
        {announcements.map(announcement => (
          <AnnouncementBox
            key={announcement.id}
            announcement={announcement}
            incidentKind={incidentKindOf(announcement, incidentConfig)}
            chosenColor={colorOf(announcement.id)}
            canEditColor={canEditColor}
            onColorChange={color => changeColor(announcement, color)}
          />
        ))}
      </div>
    );
  }

  return (
    <InfoCard
      title={title}
      variant="fullHeight"
      actionsClassName={footer.actions}
      actions={<ViewMoreLink to={ANNOUNCEMENTS_PATH} />}
    >
      {content}
    </InfoCard>
  );
};
